import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { DetailCard } from '../../../../components/ui/DetailCard';
import { formatDateTime } from '../../../../lib/date/formatDateTime';
import { usePermissions } from '../../../permissions/usePermissions';
import type { ChannelState, IntegrationStatus } from '../../types/gen-file.types';
import theme from '../../../../theme/theme';

interface GeneralChannelCardProps {
  channelState: ChannelState | null;
  onUpload: () => void;
}

const API_DOMAIN = window.__env__?.VITE_BACKEND_HOMERE_CONNECT_DOMAIN ?? '/api';

function buildDownloadUrl(version: string): string {
  return `${API_DOMAIN}/instances/version/${version}`;
}

function StatusChip({ status }: { status: IntegrationStatus }) {
  const { t } = useTranslation('generalSettings');

  const config: Partial<
    Record<IntegrationStatus, { color: 'info' | 'success' | 'error'; label: string }>
  > = {
    skipped: { color: 'info', label: t('card.status.skipped') },
    success: { color: 'success', label: t('card.status.success') },
    failed: { color: 'error', label: t('card.status.failed') },
    pending: { color: 'info', label: t('card.status.pending') },
    not_applicable: { color: 'info', label: t('card.status.not_applicable') },
  };

  const { color, label } = config[status] ?? { color: 'default' as const, label: status };
  return <Chip size="small" color={color} label={label} />;
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, py: 0.75 }}>
      <Typography
        sx={{
          width: 200,
          flexShrink: 0,
          fontSize: theme.typography.fontSizeDescription,
          color: theme.palette.text.label,
          fontWeight: theme.typography.fontWeightMedium,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

function FieldValue({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{ fontSize: theme.typography.fontSizeDescription, color: theme.palette.text.body }}
    >
      {children}
    </Typography>
  );
}

export function GeneralChannelCard({ channelState, onUpload }: GeneralChannelCardProps) {
  const { t } = useTranslation('generalSettings');
  const { can } = usePermissions();
  const canModify = can('general_settings', 'modify');

  const current = channelState?.current ?? null;
  const lastUpload = channelState?.last_upload ?? null;
  const lastUploadFailed =
    lastUpload &&
    lastUpload.integration_status === 'failed' &&
    (!current || lastUpload.version !== current.version);

  return (
    <DetailCard
      icon={
        <SettingsOutlinedIcon
          sx={{ fontSize: theme.iconSize.sm, color: theme.palette.primary.main }}
        />
      }
      iconBg="#dbeafe"
      title={t('card.title')}
      subtitle={current ? t('card.subtitle') : undefined}
      strip={theme.palette.primary.main}
    >
      {!current ? (
        <Stack spacing={2} sx={{ py: 2, alignItems: 'flex-start' }}>
          <Typography sx={{ color: theme.palette.text.subtle }}>{t('card.empty')}</Typography>
          {canModify && (
            <Button variant="contained" startIcon={<UploadFileOutlinedIcon />} onClick={onUpload}>
              {t('card.upload')}
            </Button>
          )}
        </Stack>
      ) : (
        <Stack spacing={0}>
          {/* Version hero block — like the mockup */}
          <Box sx={{ pb: 1.5 }}>
            <Typography
              sx={{
                fontSize: theme.typography.fontSizeLabel,
                fontWeight: theme.typography.fontWeightSemiBold,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: theme.palette.primary.main,
                mb: 0.5,
              }}
            >
              {t('card.version')}
            </Typography>
            <Typography
              sx={{
                fontSize: '1.5rem',
                fontWeight: theme.typography.fontWeightBold,
                color: theme.palette.text.body,
                lineHeight: 1.2,
              }}
            >
              {current.version}
            </Typography>
            <Typography
              sx={{
                fontSize: theme.typography.fontSizeDescription,
                color: theme.palette.text.muted,
                mt: 0.5,
              }}
            >
              {t('card.publishedOn')} {formatDateTime(current.uploaded_at)} {t('card.by')}{' '}
              {current.uploaded_by.username ?? t('card.deletedUser')}
            </Typography>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Detail fields */}
          <Stack spacing={0.5}>
            <FieldRow label={t('card.hcAppVersion')}>
              <FieldValue>{current.hc_app_version}</FieldValue>
            </FieldRow>

            <FieldRow label={t('card.integrationStatus')}>
              <StatusChip status={current.integration_status} />
            </FieldRow>

            <FieldRow label={t('card.downloadUrl')}>
              <Typography
                sx={{
                  fontSize: theme.typography.fontSizeDescription - 1,
                  fontFamily: 'monospace',
                  color: theme.palette.text.body,
                  wordBreak: 'break-all',
                }}
              >
                {buildDownloadUrl(current.version)}
              </Typography>
              <Typography
                sx={{
                  fontSize: theme.typography.fontSizeLabel,
                  color: theme.palette.text.subtle,
                  mt: 0.5,
                }}
              >
                {t('card.downloadUrlHint')}
              </Typography>
            </FieldRow>
          </Stack>

          {canModify && (
            <Box
              sx={{
                mt: 2.5,
                pt: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button variant="contained" startIcon={<UploadFileOutlinedIcon />} onClick={onUpload}>
                {t('card.upload')}
              </Button>
            </Box>
          )}
        </Stack>
      )}

      {lastUploadFailed && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography
            sx={{
              fontWeight: theme.typography.fontWeightSemiBold,
              fontSize: theme.typography.fontSizeDescription,
            }}
          >
            {t('card.lastUploadFailed')}
          </Typography>
          {lastUpload.integration_error && (
            <Typography sx={{ fontSize: theme.typography.fontSizeDescription, mt: 0.5 }}>
              {lastUpload.integration_error}
            </Typography>
          )}
        </Alert>
      )}
    </DetailCard>
  );
}
